import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import TopicCard from '@/pages/topics/components/TopicCard';
import { categories } from '@/mocks/categories';
import { topics } from '@/mocks/topics';
import { useSeo } from '@/hooks/useSeo';

const SITE_URL = 'https://q88p.top';

export default function TopicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'all';
  const keyword = searchParams.get('q') || '';
  const [sort, setSort] = useState<'hot' | 'date'>('hot');
  const [localQuery, setLocalQuery] = useState(keyword);

  useEffect(() => {
    setLocalQuery(keyword);
  }, [keyword]);

  useSeo({
    title: '热门话题_爆料专区最新爆料帖与讨论区 - 爆料专区导航',
    description:
      '爆料专区热门话题聚合页：按娱乐、科技、财经、社会等主题筛选最新爆料帖，支持关键词搜索与热度排序，快速找到正在发酵的讨论区内容。',
    keywords: '爆料专区热门话题,最新爆料帖,爆料专区讨论区,爆料专区分类,爆料专区网址',
    path: '/topics',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '爆料专区热门话题',
        url: `${SITE_URL}/topics`,
        inLanguage: 'zh-CN',
        description: '按主题分类聚合的最新爆料帖与热门讨论区列表。',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: topics.map((topic, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: topic.title,
            url: `${SITE_URL}/topics/${topic.id}`,
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '热门话题', item: `${SITE_URL}/topics` },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    let list = [...topics];
    if (activeCategory !== 'all') {
      list = list.filter((t) => t.category === activeCategory);
    }
    const k = keyword.trim();
    if (k) {
      list = list.filter(
        (t) =>
          t.title.includes(k) || t.summary.includes(k) || t.categoryName.includes(k) || t.author.includes(k),
      );
    }
    return list.sort((a, b) => (sort === 'hot' ? b.hot - a.hot : a.date < b.date ? 1 : -1));
  }, [activeCategory, keyword, sort]);

  const updateCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') {
      next.delete('category');
    } else {
      next.set('category', id);
    }
    setSearchParams(next);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const q = localQuery.trim();
    if (q) {
      next.set('q', q);
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  };

  const activeName = categories.find((c) => c.id === activeCategory)?.name ?? '全部话题';

  return (
    <main className="pt-16 md:pt-20">
      <section className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '热门话题' }]} />
          <h1 className="font-heading mt-5 text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            热门话题与最新爆料帖
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
            这里聚合了爆料专区各主题专区正在讨论的内容。你可以按分类筛选、按关键词搜索，也可以切换热度与时间排序，快速找到想看的方向。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <form onSubmit={handleSearch} role="search" className="flex flex-col sm:flex-row gap-2.5 max-w-2xl">
          <label htmlFor="topic-search" className="sr-only">
            搜索话题关键词
          </label>
          <input
            id="topic-search"
            type="search"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            placeholder="输入关键词，如：新品、复盘、避坑"
            className="flex-1 min-w-0 px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
          <button
            type="submit"
            className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {categories.map((cat) => {
            const isActive = cat.id === activeCategory;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => updateCategory(cat.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={cat.icon} />
                {cat.name}
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-background-200">
          <p className="text-sm text-foreground-600">
            当前：<span className="font-medium text-foreground-900">{activeName}</span>
            {keyword ? <span> · 关键词「{keyword}」</span> : null}
            <span className="text-foreground-400"> · 共 {filtered.length} 条</span>
          </p>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
            <button
              type="button"
              onClick={() => setSort('hot')}
              className={`px-1 py-1 rounded-full text-xs cursor-pointer whitespace-nowrap ${
                sort === 'hot' ? 'bg-background-50 text-primary-600 font-medium' : 'text-foreground-600'
              }`}
            >
              <span className="inline-block px-3 py-1.5">按热度</span>
            </button>
            <button
              type="button"
              onClick={() => setSort('date')}
              className={`px-1 py-1 rounded-full text-xs cursor-pointer whitespace-nowrap ${
                sort === 'date' ? 'bg-background-50 text-primary-600 font-medium' : 'text-foreground-600'
              }`}
            >
              <span className="inline-block px-3 py-1.5">按时间</span>
            </button>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <div className="mt-12 text-center py-16 bg-background-100 rounded-lg">
            <span className="mx-auto w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center text-2xl">
              <i className="ri-search-eye-line" />
            </span>
            <h2 className="font-heading mt-4 text-lg font-semibold text-foreground-900">没有找到相关话题</h2>
            <p className="mt-2 text-sm text-foreground-600">换个关键词，或点击「全部话题」查看所有内容。</p>
            <button
              type="button"
              onClick={() => {
                setLocalQuery('');
                setSearchParams(new URLSearchParams());
              }}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}

        <div className="mt-14">
          <SectionHeading
            eyebrow="More"
            title="还没看够？换个分类继续逛"
            desc="每个主题专区都在持续更新，选一个方向深入阅读，比在大厅里逐条翻更高效。"
          />
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => updateCategory(cat.id)}
                className="px-3.5 py-2 rounded-md bg-secondary-100 text-secondary-800 text-sm hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}